import React, { useRef, useEffect } from "react";
import styled from "styled-components";

export type SequencerMode = "chord" | "drone-mono" | "drone-duo";

interface ModePickerProps {
  mode: SequencerMode;
  disabled?: boolean;
  onModeChange: (mode: SequencerMode) => void;
}

const ModePicker: React.FC<ModePickerProps> = ({
  mode,
  disabled = false,
  onModeChange,
}) => {
  // Track the last used drone mode to preserve it when switching back to drone
  const lastDroneModeRef = useRef<"drone-mono" | "drone-duo">("drone-mono");

  // Update the ref when in drone mode
  useEffect(() => {
    if (mode === "drone-mono" || mode === "drone-duo") {
      lastDroneModeRef.current = mode;
    }
  }, [mode]);

  // Determine current mode (chord or drone)
  const isChordMode = mode === "chord";

  // Handle mode button click
  const handleModeClick = (selectedMode: "chord" | "drone") => {
    if (disabled) return;

    if (selectedMode === "chord") {
      onModeChange("chord");
    } else if (selectedMode === "drone") {
      // Use the last drone mode, or default to mono
      onModeChange(lastDroneModeRef.current);
    }
  };

  return (
    <ModePickerWrapper id="mode-picker">
      <ModeSelectorWrapper id="mode-selector-wrapper">
        <ModeSelector id="mode-selector">
          <ModeButton
            id="mode-chord-button"
            $active={isChordMode}
            $disabled={disabled}
            onClick={() => handleModeClick("chord")}
          >
            Chords
          </ModeButton>
          <ModeButton
            id="mode-drone-button"
            $active={!isChordMode}
            $disabled={disabled}
            onClick={() => handleModeClick("drone")}
          >
            Drone
          </ModeButton>
        </ModeSelector>
      </ModeSelectorWrapper>
    </ModePickerWrapper>
  );
};

export default ModePicker;

// Styled Components
const ModePickerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 10px 0;
`;

const ModeSelectorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModeSelector = styled.div`
  display: flex;
  align-items: stretch;
  border: 2px solid #cfc9c9;
  border-radius: 4px;
  overflow: hidden;
`;

const ModeButton = styled.button<{ $active: boolean; $disabled: boolean }>`
  background-color: ${(props) => (props.$active ? "#CFC9C9" : "transparent")};
  border: none;
  border-right: 2px solid #cfc9c9;
  color: ${(props) => (props.$active ? "#000" : "#CFC9C9")};
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  padding: 6px 10px;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  user-select: none;
  image-rendering: pixelated;
  transition: all 0.15s ease;

  &:last-child {
    border-right: none;
  }

  &:hover {
    ${(props) =>
      !props.$disabled &&
      !props.$active &&
      `
      background-color: rgba(207, 201, 201, 0.1);
    `}
  }

  &:active {
    ${(props) => !props.$disabled && "opacity: 0.8;"}
  }
`;
